import { useRef, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';

export default function ImageUploader({
  label = 'Bild',
  value,                 // current URL string
  onChange,              // (url) => void
  folder = 'images',     // server-side destination folder
  readOnly = false,
  accept = 'image/*',
  maxSizeMB = 8,
  help,
}) {
  const api = useAdminApi();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const startPick = () => !readOnly && inputRef.current?.click();

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setError('');
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`Datei ist größer als ${maxSizeMB} MB.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const url = await api.upload(file, { path: folder, onProgress: setProgress });
      onChange?.(url);
    } catch (e) {
      setError(e?.message || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (readOnly) return;
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="mb-3">
      {label && <label className="form-label">{label}</label>}

      <div
        className={`uploader-tile ${dragOver ? 'drag' : ''} ${readOnly ? 'disabled' : ''}`}
        onClick={startPick}
        onDragOver={(e) => { e.preventDefault(); !readOnly && setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
      >
        {!value && (
          <div className="uploader-empty">
            <i className="lni lni-cloud-upload"></i>
            <div>Bild hierher ziehen oder klicken zum Auswählen</div>
            <small>Akzeptiert: {accept.replace('image/', 'Bilder ')}, bis {maxSizeMB} MB</small>
          </div>
        )}

        {value && (
          <div className="uploader-preview">
            <img src={value} alt="preview" />
            {!readOnly && (
              <div className="uploader-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-edit"
                  onClick={(e) => { e.stopPropagation(); startPick(); }}
                >
                  Ersetzen
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-cancel"
                  onClick={(e) => { e.stopPropagation(); onChange?.(''); }}
                >
                  Entfernen
                </button>
              </div>
            )}
          </div>
        )}

        {uploading && (
          <div className="uploader-progress">
            <div className="progress">
              <div className="progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <small className="text-muted mt-1 d-block">Lade hoch… {progress}%</small>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />

      {help && <div className="form-text">{help}</div>}
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
}
